import type { EChartsOption } from 'echarts'
import { deltaColor, formatCompactNumber, formatShare, formatSignedShare, isFiniteNumber, relativeDelta, richSafe, tooltipBase, tooltipHtml } from './format'
import { divergingColor, labelColorOn, seriesColor, type ChartTheme } from './palette'

export interface TreemapNode {
  id: string
  name: string
  value: number | null
  previous?: number | null
  /** Подпись мельче (в подсказке). */
  sublabel?: string | null
  children?: TreemapNode[]
  /** Есть уровень ниже, даже если дети ещё не загружены (ленивый провал). */
  hasChildren?: boolean
  /** Слот палитры для `colorBy: 'entity'`. */
  slot?: number
  /** Служебный узел («Не сопоставлено»): серый и последним в раскладке. */
  muted?: boolean
}

/** Чем красить сегменты: одним цветом, изменением к сравнению (расходящаяся шкала) или цветом сущности. */
export type TreemapColorBy = 'single' | 'delta' | 'entity'

export interface TreemapInput {
  /** Узлы текущего уровня (их дети не рисуются — провал по клику). */
  nodes: TreemapNode[]
  colorBy?: TreemapColorBy
  /** Предел расходящейся шкалы для Δ (по модулю), по умолчанию 0,5. */
  deltaLimit?: number
  valueFormat?: (value: number) => string
  invertDelta?: boolean
}

/** Узел по пути id от корня; несуществующий путь обрезается на последнем найденном узле. */
export function nodeAtPath(root: TreemapNode[], path: string[]): { nodes: TreemapNode[]; trail: TreemapNode[] } {
  let nodes = root
  const trail: TreemapNode[] = []
  for (const id of path) {
    const next = nodes.find((node) => node.id === id)
    if (!next || !next.children) break
    trail.push(next)
    nodes = next.children
  }
  return { nodes, trail }
}

export function treemapFill(node: TreemapNode, index: number, input: TreemapInput, theme: ChartTheme): string {
  const colorBy = input.colorBy ?? 'single'
  if (node.muted) return theme.other
  if (colorBy === 'delta') {
    const delta = relativeDelta(node.value, node.previous)
    if (delta === null) return theme.diverging[1]
    return divergingColor(theme, input.invertDelta ? -delta : delta, input.deltaLimit ?? 0.5)
  }
  if (colorBy === 'entity') return seriesColor(theme, node.slot ?? index)
  return seriesColor(theme, 0)
}

/**
 * Прямоугольники долей текущего уровня. Размер — значение, цвет — по `colorBy`; подписи внутри только там,
 * где помещаются (ECharts прячет не влезающие), цвет подписи — контрастный к заливке. Между сегментами —
 * зазор 2 px цвета поверхности. Встроенные крошки и масштаб ECharts выключены: уровнем управляет компонент,
 * чтобы крошки страницы и графика были одними и теми же.
 */
export function buildTreemapOption(input: TreemapInput, theme: ChartTheme): EChartsOption {
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  // Порядок раскладки задаём сами: по убыванию, служебные узлы — в конце (ECharts сам их не отличит).
  const nodes = input.nodes
    .filter((node) => isFiniteNumber(node.value) && (node.value as number) > 0)
    .sort((a, b) => Number(Boolean(a.muted)) - Number(Boolean(b.muted)) || (b.value as number) - (a.value as number))
  const total = nodes.reduce((sum, node) => sum + (node.value as number), 0)

  const data = nodes.map((node, index) => {
    const fill = treemapFill(node, index, input, theme)
    const drillable = Boolean(node.hasChildren || (node.children && node.children.length > 0))
    return {
      id: node.id,
      name: node.name,
      value: node.value as number,
      itemStyle: { color: fill },
      label: { color: labelColorOn(fill, theme) },
      cursor: drillable ? 'pointer' : 'default',
    }
  })

  return {
    backgroundColor: 'transparent',
    animationDurationUpdate: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const node = nodes.find((item) => item.id === params.data?.id)
        if (!node) return ''
        const delta = relativeDelta(node.value, node.previous)
        return tooltipHtml(theme, node.sublabel ? `${node.name} · ${node.sublabel}` : node.name, [
          {
            label: 'текущий период',
            value: isFiniteNumber(node.value) ? valueFormat(node.value) : '—',
            note: total > 0 && isFiniteNumber(node.value) ? `доля ${formatShare(node.value / total)}` : undefined,
          },
          ...(node.previous !== undefined
            ? [
                {
                  label: 'сравнение',
                  value: isFiniteNumber(node.previous) ? valueFormat(node.previous) : '—',
                  note: delta !== null ? formatSignedShare(delta) : undefined,
                  noteColor: deltaColor(theme, delta, input.invertDelta),
                },
              ]
            : []),
        ])
      },
    },
    series: [
      {
        type: 'treemap',
        data,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        roam: false,
        nodeClick: false,
        leafDepth: 1,
        breadcrumb: { show: false },
        sort: false,
        squareRatio: 0.5 * (1 + Math.sqrt(5)),
        itemStyle: { borderColor: theme.surface, borderWidth: 2, gapWidth: 2, borderRadius: 4 },
        label: {
          show: true,
          position: 'insideTopLeft',
          padding: [6, 8],
          overflow: 'truncate',
          fontFamily: theme.fontFamily,
          formatter: (params: any) => {
            const node = nodes.find((item) => item.id === params.data?.id)
            if (!node || !isFiniteNumber(node.value)) return richSafe(params.name)
            const share = total > 0 ? ` · ${formatShare(node.value / total)}` : ''
            return `{name|${richSafe(params.name)}}\n{value|${richSafe(valueFormat(node.value))}${share}}`
          },
          rich: {
            name: { fontSize: 12, fontWeight: 600, lineHeight: 16 },
            value: { fontSize: 11, lineHeight: 15, opacity: 0.85 },
          },
        },
        upperLabel: { show: false },
        emphasis: { itemStyle: { borderColor: theme.text, borderWidth: 1 } },
      },
    ],
  } as EChartsOption
}
